import { Heading, Icon } from '@alto-avios/alto-ui'
import {
  getCarHireCancellationPolicy,
  getExperienceCancellationPolicy,
  getFlightCancellationPolicy,
  getHotelCancellationPolicy,
} from '@iagl/order-orders-cancellation-policies'
import { Modal } from '@reward-platform/lift/components'
import { BasketItemType } from '@reward-platform/order-schemas/basket'
import {
  CarHireItemType,
  ExperienceItemType,
  FlightItemType,
  HotelItemType,
} from '@reward-platform/order-schemas/common'
import { isArray } from 'lodash'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import React, { useId } from 'react'
import { useLocalisation } from '~/context/localisation'
import { useUser } from '~/context/user'
import { useBasket } from '~/hooks/useBasket'
import { CancellationPolicyTemplates } from '~/hooks/useCancellationPolicy/types'
import useCancellationPolicyContent from '~/hooks/useCancellationPolicy/useCancellationPolicyContent'
import { formatLegacyMessage } from '~/hooks/useIntl/useIntl'
import styles from './CancellationPolicyModal.module.scss'
import { ModalProps, ShowPoliciesByProductType } from './CancellationPolicyModal.types'

type PolicyProps = {
  productType: BasketItemType
  policiesToShow: ShowPoliciesByProductType
  templates: CancellationPolicyTemplates | undefined
}

const getModalContentByProductType = (
  productType: BasketItemType,
  policiesToShow: ShowPoliciesByProductType,
  locale: string,
  currency: string,
  templates: CancellationPolicyTemplates | undefined
) => {
  if (!templates || !currency) {
    return null
  }
  const options = { currency, locale }
  const formatValues = { userCurrency: currency }

  switch (productType) {
    case HotelItemType: {
      const hotelPolicy = policiesToShow[productType]
      if (!hotelPolicy?.show) {
        return null
      }

      const cancellations = getHotelCancellationPolicy({
        packageRules: hotelPolicy.packageRules || [],
        options,
        templates,
        duration: hotelPolicy.duration,
      })
      return cancellations
        .map(
          ({ id, message }) =>
            id &&
            formatLegacyMessage(
              {
                id,
                defaultMessage: message,
              },
              formatValues
            )
        )
        .filter(Boolean)
    }
    case CarHireItemType: {
      const carHirePolicy = policiesToShow[productType]
      if (!carHirePolicy?.show) {
        return null
      }

      const message = getCarHireCancellationPolicy({ options, template: templates })

      return formatLegacyMessage(
        {
          id: 'carHireDefault',
          defaultMessage: message,
        },
        formatValues
      )
    }

    case FlightItemType: {
      const flightPolicy = policiesToShow[productType]
      if (!flightPolicy?.show) {
        return null
      }

      const message = getFlightCancellationPolicy({ options, template: templates })

      return formatLegacyMessage(
        {
          id: 'flightDefault',
          defaultMessage: message,
        },
        formatValues
      )
    }
    case ExperienceItemType: {
      const experiencePolicy = policiesToShow[productType]

      if (!experiencePolicy?.show) {
        return null
      }
      const message = getExperienceCancellationPolicy({
        locale,
        rules: experiencePolicy.cancellationPolicy,
        templates,
      })

      return formatLegacyMessage(
        {
          id: 'experienceDefault',
          defaultMessage: message,
        },
        formatValues
      )
    }
    default:
      return null
  }
}

const PolicyItemList = ({ productType, policiesToShow, templates }: PolicyProps) => {
  const { userCurrency } = useUser()
  const { locale } = useLocalisation()
  const {
    basket: { data: basketData },
  } = useBasket()
  const currency = basketData?.currency ?? userCurrency

  const items = [
    getModalContentByProductType(productType, policiesToShow, locale, currency, templates),
  ]
    .flat()
    .filter((val) => {
      // This is handling the fact that hotel policies come in an array format
      // while the other policies don't.
      return val !== null && (typeof val === 'string' || isArray(val))
    })

  return (
    <ul className={styles.list}>
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  )
}

const CarHireConditionsOfRental = ({ productType, policiesToShow, templates }: PolicyProps) => {
  const t = useTranslations('components.cancellation_policy')
  const refundHeadingId = useId()
  const rentalConditionsHeadingId = useId()
  const carHirePolicies = policiesToShow.CAR_HIRE
  const conditionsOfRental = carHirePolicies?.show
    ? [
        carHirePolicies.locationPolicy,
        carHirePolicies.rentalPolicy,
        carHirePolicies.locationRentalPolicy,
      ]
    : []
  const filteredConditionsOfRental = conditionsOfRental.filter(
    (item): item is NonNullable<(typeof conditionsOfRental)[number]> =>
      Boolean(item && Object.keys(item).length > 0)
  )

  const getCancelationPolicyLinkText = (policyType: string | undefined) => {
    if (!policyType) {
      return ''
    }
    switch (policyType) {
      case 'rental':
        return t('labels.rental_link_text')
      case 'location':
        return t('labels.location_link_text')
      case 'locationRental':
        return t('labels.location_rental_link_text')
      default:
        return ''
    }
  }

  return (
    <div className={styles.cancellationFeesAndPoliciesContainer}>
      <section aria-labelledby={refundHeadingId}>
        <Heading as="h4" id={refundHeadingId}>
          {t('titles.car_hire_modal_refund')}
        </Heading>
        <PolicyItemList
          productType={productType}
          policiesToShow={policiesToShow}
          templates={templates}
        />
      </section>
      {filteredConditionsOfRental?.length > 0 && (
        <section aria-labelledby={rentalConditionsHeadingId}>
          <Heading as="h4" id={rentalConditionsHeadingId}>
            {t('labels.rental_conditions')}
          </Heading>
          <ul className={styles.policiesContainer}>
            {filteredConditionsOfRental.map(({ policyType, policyLink }) =>
              policyLink ? (
                <li key={policyLink}>
                  <Link
                    target="_blank"
                    rel="noopener noreferrer"
                    href={policyLink ?? ''}
                    className={styles.policyLink}
                  >
                    {getCancelationPolicyLinkText(policyType)}
                    <Icon
                      iconName="arrow-up-right-from-square"
                      iconSize="0.5x"
                      aria-hidden="true"
                    />
                  </Link>
                </li>
              ) : null
            )}
          </ul>
        </section>
      )}
    </div>
  )
}

export const CancellationPolicyModal: React.FC<ModalProps> = ({
  id,
  policiesToShow,
  isOpen,
  onClose,
}) => {
  const { productCancellationTitles, templates } = useCancellationPolicyContent()
  const policyList = Object.entries(policiesToShow).filter(([_, value]) => Boolean(value?.show))

  if (policyList.length < 1) {
    throw new Error('At least one policy must be displayed')
  }

  const hasMultipleProducts = policyList.length > 1

  if (hasMultipleProducts) {
    return (
      <Modal
        id={id}
        onClose={onClose}
        isOpen={isOpen}
        title={productCancellationTitles.default}
        titleTextAlign="left"
        headingTag="h1"
        zIndex={1401}
      >
        {policyList.map(([itemTypeString]) => {
          const itemType = itemTypeString as BasketItemType
          const sectionTitleId = `${
            id ?? 'cancellation-policy-modal'
          }-${itemType.toLowerCase()}-title`

          return (
            <section key={itemType} aria-labelledby={sectionTitleId}>
              <Heading as="h3" id={sectionTitleId} fontWeight={500}>
                {productCancellationTitles[itemType]}
              </Heading>
              {itemType === 'CAR_HIRE' ? (
                <CarHireConditionsOfRental
                  productType={itemType}
                  policiesToShow={policiesToShow}
                  templates={templates}
                />
              ) : (
                <PolicyItemList
                  productType={itemType}
                  policiesToShow={policiesToShow}
                  templates={templates}
                />
              )}
              <div className={styles.spacer} />
            </section>
          )
        })}
      </Modal>
    )
  }

  const productPolicy = policyList[0]
  const itemType = productPolicy[0] as BasketItemType
  const title = productCancellationTitles[itemType]

  if (itemType === 'CAR_HIRE') {
    return (
      <Modal
        id={id}
        onClose={onClose}
        isOpen={isOpen}
        title={title}
        isCentered
        headingTag="h1"
        zIndex={1401}
      >
        <CarHireConditionsOfRental
          productType={itemType}
          policiesToShow={policiesToShow}
          templates={templates}
        />
      </Modal>
    )
  }

  return (
    <Modal
      id={id}
      onClose={onClose}
      isOpen={isOpen}
      title={title}
      isCentered
      headingTag="h1"
      zIndex={1401}
    >
      <PolicyItemList
        productType={itemType}
        policiesToShow={policiesToShow}
        templates={templates}
      />
    </Modal>
  )
}
